import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { sortBy } from 'webapp/utils/common';

import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectBoardComponent from 'webapp/components/form/select-board';

import Board from 'webapp/models-custom/board';

const enum ChoiceType {
	NEW = 0,
	EXISTING = 1,
}

export const NewBoardId = '-1';

interface Args {
	changeSelectedBoardId: (id: string) => void;
	preselectedBoardId?: string;
	defaultBoardChoice?: number;
	requiredFreeSpaces?: number;
}

export default class FormSelectBoardWithCreateNewOptionComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;

	@tracked choiceType: ChoiceType;
	@tracked selectedBoardInList = ''; // the one in dropdown, end result still dependent on choiceType

	@tracked isLoadingBlocks = false;
	@tracked blocksCountPerBoard: APIData.Boards.BlocksCountResponse = {};
	requiredFreeSpaces: number;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.requiredFreeSpaces = this.args.requiredFreeSpaces || 1;
		this.choiceType = this.args.defaultBoardChoice ?? ChoiceType.NEW;

		this.selectedBoardInList = this.args.preselectedBoardId || this.sortedBoards[0]?.id || '';

		this.updateBlocksCount();

		// To ensure account templates are loaded
		void this.mmTemplatesService.requestBoardAccountTemplates();
	}

	get isNewBoardChosen() {
		return this.choiceType === ChoiceType.NEW;
	}

	get selectedBoardId() {
		return this.isNewBoardChosen ? NewBoardId : this.selectedBoardInList;
	}

	get allowBoardSelect() {
		return this.mmBoardsService.normalBoardModels.length > 0;
	}

	get choiceTypeOptions() {
		return [
			{
				value: ChoiceType.NEW,
				label: 'New dashboard',
			},
			{
				value: ChoiceType.EXISTING,
				label: 'Select one of existing dashboards',
				disabled: !this.allowBoardSelect,
			},
		];
	}

	@cached
	get boards() {
		const templateBoards = this.mmBoardsService.accountTemplateBoardModels.filter(
				(item) => item.canCurrentUserEdit,
			),
			normalBoards = this.mmBoardsService.normalBoardModels.filter((board) => {
				return board.canCurrentUserEdit && !board.isFake;
			});
		return normalBoards.concat(templateBoards);
	}

	@cached
	get sortedBoards() {
		return sortBy(sortBy(this.boards, 'isAccountTemplate').reverse(), 'order');
	}

	@cached
	get boardsList() {
		const boards = this.boards,
			boardsList: OptionRaw<string>[] = [],
			isAnyAccountTemplateShown = !!boards.find((item) => item.isAccountTemplate);

		this.sortedBoards.forEach((board) => {
			const favoriteIcon = board.favorite
					? '<ui-icon name="StarFill" size="sm"></ui-icon>'
					: '',
				templateLabel = board.isAccountTemplate
					? '<span class="outline-blue-label outline-blue-label--template"></span>'
					: '';

			let isDisabled = !board.canCurrentUserEdit,
				warning = favoriteIcon;

			// disable full boards (>=32)
			const boardFreeSpaces = Board.maxBlocks - (this.blocksCountPerBoard[board.id!] || 0);
			if (
				!this.accountService.isPredefinedBlocksSpace &&
				boardFreeSpaces < this.requiredFreeSpaces
			) {
				warning += '<span class="warn">This dashboard is full</span>';
				isDisabled = true;
			} else if (board.id === this.args.preselectedBoardId) {
				const currentType = board.isAccountTemplate ? '' : ' dashboard';
				warning = `<span class="current">Current${currentType}</span>${templateLabel}${favoriteIcon}`;
			} else if (board.isAccountTemplate) {
				warning = templateLabel;
			}

			let data: OptionRaw<string> = {
				value: board.id!,
				label: board.name,
				label2: board.sourcesText,
				label3: warning,
				avatar: board.imageMediumOrDefault,
				color: board.color,
				disabled: isDisabled,
			};

			if (isAnyAccountTemplateShown) {
				data = { ...data, group: board.isAccountTemplate ? 'Templates' : 'Dashboards' };
			}

			boardsList.push(data);
		});

		if (boardsList.length) {
			// select first option when none or disabled is selected
			if (
				!this.selectedBoardInList ||
				boardsList.find((l) => l.value === this.selectedBoardInList)?.disabled
			) {
				next(this, this.selectFirstBoardInList);
			}
		}

		return boardsList;
	}

	@action
	changeSelectedBoardInList(selectedBoardInList: string) {
		this.selectedBoardInList = selectedBoardInList;
		this.triggerChange();
	}

	@action
	changeChoiceType(choiceType: AnySimpleValue) {
		this.choiceType = choiceType as ChoiceType;
		this.triggerChange();
	}

	selectFirstBoardInList() {
		this.changeSelectedBoardInList(
			this.boardsList.find((board) => !board.disabled)?.value || '',
		);
	}

	triggerChange() {
		this.args.changeSelectedBoardId(this.selectedBoardId);
	}

	updateBlocksCount() {
		this.isLoadingBlocks = true;
		this.requestService
			.api<APIData.Boards.BlocksCountResponse>('GET', '/boards/blocks-count')
			.then((reply) => {
				this.blocksCountPerBoard = reply || {};
			})
			.finally(() => {
				this.isLoadingBlocks = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="row select-board-with-create-new">
			<FormRadiobuttonGroupComponent
				class="radiobutton-default"
				@name="boardChoice"
				@selected={{this.choiceType}}
				@options={{this.choiceTypeOptions}}
				@onChange={{this.changeChoiceType}}
			/>

			{{#if this.allowBoardSelect}}

				<div class="dropdown">
					<FormSelectBoardComponent
						name="boards"
						@selected={{this.selectedBoardInList}}
						@options={{this.boardsList}}
						@onChange={{this.changeSelectedBoardInList}}
						@placeholder="Select dashboard"
						@isNumeric={{false}}
						@isRequired={{true}}
						@extraClass="cs-image"
						@dropdownSearchPlaceholder="Search"
						@showFloatlabel={{false}}
						@tooltipOnSelectionFromProperty=""
						@isLoading={{this.isLoadingBlocks}}
						@isDisabled={{this.isNewBoardChosen}}
					/>
				</div>

			{{/if}}
		</div>
	</template>
}
